import { useState } from 'react';
import { Label } from '@/components/design-system/Label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/design-system/radio-group';
import { Button } from '@/components/design-system/Button';
import { Calendar } from '@/components/design-system/Calendar';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/design-system/Form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/design-system/popover';
import { cn } from '@/utils/styles';
import { format } from 'date-fns';
import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import { TextField } from '@design-system/DataForm/TextField';
import usePersonalInfo from '@/hooks/registration/usePersonalInfo';
import { RegistrationTrack } from '@/hooks/useRegistrationStore';
import * as P from '@/constants/paths';
import { DOBConfirmationDialog } from '@/components/screens/QuestionnairesPage/question-types/DOBConfirmationDialog';

export const RegistrationPersonalInformation = () => {
  const { form, onSubmit } = usePersonalInfo();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showDOBConfirmation, setShowDOBConfirmation] = useState(false);
  const [dobConfirmationYear, setDOBConfirmationYear] = useState<number>(0);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);

  return (
    <>
      <RegistrationScreen
        form={form}
        title="Personal Information"
        description="Please provide accurate information of the person who will be taking
            the test. We may use information below for contact purpose."
        content={
          <div className="mt-8" data-testid="registrationPersonalInfo-content">
            <TextField
              name="firstName"
              placeholder="First name"
              className="mt-5"
              autoComplete="off"
              data-testid="registrationPersonalInfo-firstNameInput"
            />
            <TextField
              name="lastName"
              placeholder="Last name"
              className="mt-5"
              autoComplete="off"
              data-testid="registrationPersonalInfo-lastNameInput"
            />
            <FormField
              control={form.control}
              name="birthDay"
              render={({ field }) => (
                <FormItem className="flex flex-col mt-5 mb-5" data-testid="registrationPersonalInfo-birthdayField">
                  <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={'outline'}
                          className={cn(
                            'w-full h-[68px] pl-3 text-left font-normal justify-start bg-overlay-primary',
                            !field.value && 'text-muted-foreground',
                          )}
                          data-testid="registrationPersonalInfo-birthdayButton"
                        >
                          <div className="flex flex-col items-start w-full">
                            <span className="text-sm font-medium text-tertiary">
                              Birthday
                            </span>
                            {field.value instanceof Date &&
                            !isNaN(field.value.getTime()) ? (
                              <span>{format(field.value, 'PPP')}</span>
                            ) : (
                              <div className="flex flex-row justify-between text-secondary-foreground mt-2">
                                <span>MM/DD/YYYY</span>
                              </div>
                            )}
                          </div>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="p-0 w-auto"
                      sideOffset={8}
                      avoidCollisions={true}
                      collisionPadding={10}
                      side="bottom"
                      data-testid="registrationPersonalInfo-birthdayCalendar"
                    >
                      <Calendar
                        mode="single"
                        captionLayout="dropdown"
                        selected={field.value || undefined}
                        defaultMonth={field.value || new Date()}
                        onSelect={(date) => {
                          if (date) {
                            const selectedYear = date.getFullYear();
                            const currentYear = new Date().getFullYear();
                            
                            // If user selected current year, show confirmation dialog
                            if (selectedYear === currentYear) {
                              setPendingDate(date);
                              setDOBConfirmationYear(currentYear);
                              setShowDOBConfirmation(true);
                              setPopoverOpen(false);
                            } else {
                              field.onChange(date);
                              setPopoverOpen(false);
                            }
                          } else {
                            field.onChange(null);
                            setPopoverOpen(false);
                          }
                        }}
                        disabled={(date) =>
                          date > new Date() || date < new Date('1900-01-01')
                        }
                        initialFocus
                        fromYear={1925}
                        toYear={new Date().getFullYear()}
                        classNames={{
                          caption:
                            'flex justify-center relative items-center pt-0',
                          cell: 'relative text-center text-sm focus-within:relative focus-within:z-20 rounded-r-md p-1',
                        }}
                        className="p-0"
                        showOutsideDays={false}
                        toMonth={new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Label className="mt-5" data-testid="registrationPersonalInfo-genderLabel">Biological Sex</Label>
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem className="flex flex-col mt-5 mb-5" data-testid="registrationPersonalInfo-genderField">
                  <FormControl>
                    <RadioGroup
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                      name="gender"
                      data-testid="registrationPersonalInfo-genderRadioGroup"
                    >
                      <div className="flex items-center space-x-2" data-testid="registrationPersonalInfo-genderMale">
                        <RadioGroupItem value="Male" id="male" data-testid="registrationPersonalInfo-genderMaleRadio" />
                        <Label htmlFor="male">Male</Label>
                      </div>
                      <div className="flex items-center space-x-2" data-testid="registrationPersonalInfo-genderFemale">
                        <RadioGroupItem value="Female" id="female" data-testid="registrationPersonalInfo-genderFemaleRadio" />
                        <Label htmlFor="female">Female</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        }
        previousPath={P.REGISTRATION_ACCOUNT_CREATION}
        primaryActionLabel="Next"
        primaryActionIsSubmit
        formOnValid={onSubmit}
        checkForRegisterStore={RegistrationTrack.PersonalInformation}
        data-testid="registrationPersonalInfo-screen"
      />
      <DOBConfirmationDialog
        open={showDOBConfirmation}
        onOpenChange={(open) => {
          setShowDOBConfirmation(open);
          if (!open && pendingDate) {
            // If dialog is closed without confirmation, clear the date field
            form.setValue('birthDay', null);
            setPendingDate(null);
          }
        }}
        year={dobConfirmationYear}
        onConfirm={() => {
          if (pendingDate) {
            form.setValue('birthDay', pendingDate);
            setPendingDate(null);
          }
          setShowDOBConfirmation(false);
        }}
        onReject={() => {
          // Clear the date field when user rejects
          form.setValue('birthDay', null);
          setPendingDate(null);
          setShowDOBConfirmation(false);
        }}
      />
    </>
  );
};
